import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calculator, Plus, Trash2, TrendingUp, TrendingDown, Receipt, ArrowUpRight, ArrowDownRight, Package, Boxes, Link2, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const KDV_RATES = [
  { value: "1", label: "%1" },
  { value: "10", label: "%10" },
  { value: "20", label: "%20" },
];

const CATEGORIES = [
  { value: "prob", label: "Prob" },
  { value: "sternafix", label: "Sternafix" },
  { value: "cihaz", label: "Cihaz" },
  { value: "genel", label: "Genel" },
  { value: "diger", label: "Diğer" },
];

const SOURCE_LABELS = {
  manual: "Manuel",
  probe_invoice: "Prob Fatura",
  sternafix_invoice: "Sternafix Fatura",
};

const Finance = () => {
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState(null);
  const [products, setProducts] = useState([]);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [filterType, setFilterType] = useState("all");
  const [filterYear, setFilterYear] = useState(() => new Date().getFullYear().toString());
  const [filterMonth, setFilterMonth] = useState("all");
  const [formData, setFormData] = useState({
    record_type: "satis",
    description: "",
    amount: 0,
    kdv_rate: 20,
    record_date: "",
    category: "genel",
    product_id: "",
    quantity: 0,
    notes: "",
  });

  const fetchData = async () => {
    try {
      const params = new URLSearchParams();
      if (filterType !== "all") params.append("record_type", filterType);
      if (filterYear !== "all") params.append("year", filterYear);
      if (filterMonth !== "all") params.append("month", filterMonth);

      const [recordsRes, summaryRes] = await Promise.all([
        axios.get(`${API}/finance?${params.toString()}`),
        axios.get(`${API}/finance/summary?${filterYear !== "all" ? `year=${filterYear}` : ""}${filterMonth !== "all" ? `&month=${filterMonth}` : ""}`),
      ]);
      setRecords(recordsRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error("Error fetching finance data:", error);
      toast.error("Finans verileri yüklenirken hata oluştu");
    }
  };

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${API}/products`);
      setProducts(res.data);
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filterType, filterYear, filterMonth]);

  useEffect(() => {
    fetchProducts();
  }, []);

  const handleSyncInvoices = async () => {
    setIsSyncing(true);
    try {
      const res = await axios.post(`${API}/finance/sync-invoices`);
      const count = res.data.synced_count;
      if (count > 0) {
        toast.success(`${count} fatura kaydı Finans'a aktarıldı!`);
        fetchData();
      } else {
        toast.info("Tüm faturalar zaten senkronize.");
      }
    } catch (error) {
      toast.error("Senkronizasyon sırasında hata oluştu");
    } finally {
      setIsSyncing(false);
    }
  };

  const handleProductSelect = (value) => {
    if (value === "manual") {
      setFormData(prev => ({ ...prev, product_id: "", quantity: 0 }));
      return;
    }
    const product = products.find(p => p.id === value);
    if (product) {
      setFormData(prev => ({
        ...prev,
        product_id: product.id,
        description: product.name,
        amount: product.unit_price,
        category: product.category,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        record_type: formData.record_type,
        description: formData.description,
        amount: formData.amount,
        kdv_rate: formData.kdv_rate,
        record_date: formData.record_date,
        category: formData.category,
        notes: formData.notes,
      };
      if (formData.product_id) {
        payload.product_id = formData.product_id;
        payload.quantity = formData.quantity;
      }
      await axios.post(`${API}/finance`, payload);
      toast.success(formData.record_type === "satis" ? "Satış kaydı eklendi" : "Alış kaydı eklendi");
      if (formData.product_id && formData.quantity > 0) {
        const action = formData.record_type === "alis" ? "Stok artırıldı" : "Stok düşürüldü";
        toast.info(`${action}: ${formData.quantity} adet`);
      }
      setIsAddDialogOpen(false);
      resetForm();
      fetchData();
      fetchProducts();
    } catch (error) {
      console.error("Error adding finance record:", error);
      toast.error("Kayıt eklenirken hata oluştu");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu kaydı silmek istediğinizden emin misiniz?")) return;
    try {
      await axios.delete(`${API}/finance/${id}`);
      toast.success("Kayıt silindi");
      fetchData();
    } catch (error) {
      toast.error("Kayıt silinirken hata oluştu");
    }
  };

  const resetForm = () => {
    setFormData({ record_type: "satis", description: "", amount: 0, kdv_rate: 20, record_date: "", category: "genel", product_id: "", quantity: 0, notes: "" });
  };

  const previewKdv = formData.amount * formData.kdv_rate / 100;
  const previewTotal = formData.amount + previewKdv;

  const years = [2024, 2025, 2026, 2027];
  const months = [
    { value: "1", label: "Ocak" }, { value: "2", label: "Şubat" }, { value: "3", label: "Mart" },
    { value: "4", label: "Nisan" }, { value: "5", label: "Mayıs" }, { value: "6", label: "Haziran" },
    { value: "7", label: "Temmuz" }, { value: "8", label: "Ağustos" }, { value: "9", label: "Eylül" },
    { value: "10", label: "Ekim" }, { value: "11", label: "Kasım" }, { value: "12", label: "Aralık" },
  ];

  return (
    <div className="space-y-6" data-testid="finance-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight" data-testid="finance-title">
            Finans
          </h1>
          <p className="text-base text-muted-foreground mt-2">
            Muhasebe, KDV hesaplama ve gelir vergisi takibi
          </p>
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={handleSyncInvoices}
            disabled={isSyncing}
            data-testid="sync-invoices-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${isSyncing ? 'animate-spin' : ''}`} />
            {isSyncing ? "Senkronize ediliyor..." : "Faturaları Aktar"}
          </Button>
          <Button onClick={() => { resetForm(); setIsAddDialogOpen(true); }} data-testid="add-finance-btn">
            <Plus className="w-4 h-4 mr-2" />
            Yeni Kayıt Ekle
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="finance-summary">
          <Card className="bg-green-50 border-green-200">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-600 font-medium">Toplam Satış</p>
                  <p className="text-2xl font-bold text-green-700 mt-1">
                    {summary.total_sales_with_kdv.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL
                  </p>
                  <p className="text-xs text-green-600 mt-1">
                    KDV: {summary.total_sales_kdv.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL ({summary.sales_count} kayıt)
                  </p>
                </div>
                <ArrowUpRight className="w-8 h-8 text-green-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-red-50 border-red-200">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-red-600 font-medium">Toplam Alış</p>
                  <p className="text-2xl font-bold text-red-700 mt-1">
                    {summary.total_purchases_with_kdv.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL
                  </p>
                  <p className="text-xs text-red-600 mt-1">
                    KDV: {summary.total_purchases_kdv.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL ({summary.purchases_count} kayıt)
                  </p>
                </div>
                <ArrowDownRight className="w-8 h-8 text-red-400" />
              </div>
            </CardContent>
          </Card>

          <Card className={`border-2 ${summary.net_kdv >= 0 ? 'bg-orange-50 border-orange-300' : 'bg-blue-50 border-blue-300'}`}>
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm font-medium ${summary.net_kdv >= 0 ? 'text-orange-600' : 'text-blue-600'}`}>
                    {summary.net_kdv >= 0 ? "Ödenecek KDV" : "Devreden KDV"}
                  </p>
                  <p className={`text-2xl font-bold mt-1 ${summary.net_kdv >= 0 ? 'text-orange-700' : 'text-blue-700'}`}>
                    {Math.abs(summary.net_kdv).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">Satış KDV - Alış KDV</p>
                </div>
                <Receipt className="w-8 h-8 text-orange-400" />
              </div>
            </CardContent>
          </Card>

          <Card className={`border-2 ${summary.net_profit >= 0 ? 'bg-emerald-50 border-emerald-300' : 'bg-gray-50 border-gray-300'}`}>
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-emerald-600 font-medium">Gelir Vergisi (%25)</p>
                  <p className="text-2xl font-bold text-emerald-700 mt-1">
                    {summary.income_tax.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Net Kar: {summary.net_profit.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL
                  </p>
                </div>
                <Calculator className="w-8 h-8 text-emerald-400" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex gap-3 flex-wrap items-center">
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-40" data-testid="filter-type">
                <SelectValue placeholder="Tür" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tümü</SelectItem>
                <SelectItem value="satis">Satış</SelectItem>
                <SelectItem value="alis">Alış</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterYear} onValueChange={setFilterYear}>
              <SelectTrigger className="w-32" data-testid="filter-year">
                <SelectValue placeholder="Yıl" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Yıllar</SelectItem>
                {years.map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterMonth} onValueChange={setFilterMonth}>
              <SelectTrigger className="w-32" data-testid="filter-month">
                <SelectValue placeholder="Ay" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Aylar</SelectItem>
                {months.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Records Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Receipt className="w-5 h-5" />
            Kayıtlar ({records.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="finance-table">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-4 font-semibold text-sm">Tür</th>
                  <th className="text-left py-3 px-4 font-semibold text-sm">Açıklama</th>
                  <th className="text-left py-3 px-4 font-semibold text-sm">Kategori</th>
                  <th className="text-center py-3 px-4 font-semibold text-sm">Adet</th>
                  <th className="text-right py-3 px-4 font-semibold text-sm">Tutar</th>
                  <th className="text-center py-3 px-4 font-semibold text-sm">KDV</th>
                  <th className="text-right py-3 px-4 font-semibold text-sm">Toplam</th>
                  <th className="text-left py-3 px-4 font-semibold text-sm">Tarih</th>
                  <th className="text-center py-3 px-4 font-semibold text-sm">Kaynak</th>
                  <th className="text-center py-3 px-4 font-semibold text-sm">İşlem</th>
                </tr>
              </thead>
              <tbody>
                {records
                  .sort((a, b) => new Date(b.record_date) - new Date(a.record_date))
                  .map((record) => (
                    <tr
                      key={record.id}
                      className={`border-b border-border table-row ${record.record_type === 'satis' ? 'hover:bg-green-50' : 'hover:bg-red-50'}`}
                      data-testid={`finance-row-${record.id}`}
                    >
                      <td className="py-3 px-4">
                        {record.record_type === "satis" ? (
                          <span className="flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-green-100 text-green-700 font-medium w-fit">
                            <TrendingUp className="w-3 h-3" /> Satış
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-red-100 text-red-700 font-medium w-fit">
                            <TrendingDown className="w-3 h-3" /> Alış
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 font-medium">
                        <div>
                          {record.description}
                          {record.product_name && (
                            <span className="flex items-center gap-1 text-xs text-blue-600 mt-1">
                              <Boxes className="w-3 h-3" /> {record.product_name}
                            </span>
                          )}
                          {record.notes && <p className="text-xs text-muted-foreground mt-1">{record.notes}</p>}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-xs px-2 py-1 rounded bg-slate-100 text-slate-700 font-medium">
                          {CATEGORIES.find(c => c.value === record.category)?.label || record.category}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        {record.quantity > 0 ? (
                          <span className="font-medium">{record.quantity}</span>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-right font-medium">
                        {(record.amount || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-700 font-bold">
                          %{record.kdv_rate}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right font-bold">
                        {(record.total_amount || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL
                      </td>
                      <td className="py-3 px-4 text-muted-foreground">
                        {new Date(record.record_date).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </td>
                      <td className="py-3 px-4 text-center">
                        {record.source && record.source !== "manual" ? (
                          <span className="flex items-center justify-center gap-1 text-xs px-2 py-1 rounded-full bg-purple-100 text-purple-700 font-medium">
                            <Link2 className="w-3 h-3" />
                            {SOURCE_LABELS[record.source] || record.source}
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">Manuel</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(record.id)}
                          data-testid={`delete-finance-${record.id}`}
                          className="text-destructive hover:text-destructive hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
            {records.length === 0 && (
              <div className="text-center py-12" data-testid="no-finance-records">
                <Calculator className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">Henüz finans kaydı bulunmamaktadır</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Add Record Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="add-finance-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calculator className="w-5 h-5" />
              Yeni Finans Kaydı
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Type Toggle */}
            <div>
              <Label>Kayıt Türü *</Label>
              <div className="flex gap-2 mt-2">
                <Button
                  type="button"
                  variant={formData.record_type === "satis" ? "default" : "outline"}
                  className={`flex-1 ${formData.record_type === "satis" ? "bg-green-600 hover:bg-green-700" : ""}`}
                  onClick={() => setFormData({ ...formData, record_type: "satis" })}
                  data-testid="type-satis-btn"
                >
                  <TrendingUp className="w-4 h-4 mr-2" />
                  Satış (Gelir)
                </Button>
                <Button
                  type="button"
                  variant={formData.record_type === "alis" ? "default" : "outline"}
                  className={`flex-1 ${formData.record_type === "alis" ? "bg-red-600 hover:bg-red-700" : ""}`}
                  onClick={() => setFormData({ ...formData, record_type: "alis" })}
                  data-testid="type-alis-btn"
                >
                  <TrendingDown className="w-4 h-4 mr-2" />
                  Alış (Gider)
                </Button>
              </div>
            </div>

            {/* Product Selection */}
            <div>
              <Label className="flex items-center gap-2">
                <Package className="w-4 h-4 text-blue-500" />
                Envanterden Ürün Seçin
              </Label>
              <Select value={formData.product_id || "manual"} onValueChange={handleProductSelect}>
                <SelectTrigger data-testid="finance-product-select" className="mt-1">
                  <SelectValue placeholder="Envanterden seçin veya manuel girin" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">Manuel Giriş (Envanter dışı)</SelectItem>
                  {products.map(p => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name} ({p.category}) - Stok: {p.stock_quantity} - {p.unit_price.toLocaleString('tr-TR')} TL
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {formData.product_id && (
              <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 text-sm space-y-2">
                <div className="flex items-center gap-2">
                  <Boxes className="w-4 h-4 text-blue-600" />
                  <span className="font-medium text-blue-700">
                    {products.find(p => p.id === formData.product_id)?.name}
                  </span>
                </div>
                <p className="text-blue-600 text-xs">
                  {formData.record_type === "alis"
                    ? "Alış kaydıyla stok otomatik artırılacak"
                    : "Satış kaydıyla stok otomatik düşürülecek"}
                </p>
              </div>
            )}

            {formData.product_id && (
              <div>
                <Label htmlFor="quantity">Adet *</Label>
                <Input
                  id="quantity"
                  type="number"
                  min="1"
                  data-testid="finance-quantity-input"
                  placeholder="Kaç adet?"
                  value={formData.quantity}
                  onChange={(e) => {
                    const qty = parseInt(e.target.value) || 0;
                    const product = products.find(p => p.id === formData.product_id);
                    const unitPrice = product?.unit_price || 0;
                    setFormData({ ...formData, quantity: qty, amount: round2(unitPrice * qty) });
                  }}
                  required
                />
              </div>
            )}

            <div>
              <Label htmlFor="description">Açıklama *</Label>
              <Input
                id="description"
                data-testid="finance-description-input"
                placeholder="Örn: Prob satışı - Bursa Devlet Hastanesi"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="category">Kategori</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                  <SelectTrigger data-testid="finance-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="record_date">Tarih *</Label>
                <Input
                  id="record_date"
                  type="date"
                  data-testid="finance-date-input"
                  value={formData.record_date}
                  onChange={(e) => setFormData({ ...formData, record_date: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="amount">Tutar (KDV Hariç) *</Label>
                <Input
                  id="amount"
                  type="number"
                  min="0"
                  step="0.01"
                  data-testid="finance-amount-input"
                  placeholder="0.00"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) || 0 })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="kdv_rate">KDV Oranı *</Label>
                <Select value={formData.kdv_rate.toString()} onValueChange={(v) => setFormData({ ...formData, kdv_rate: parseInt(v) })}>
                  <SelectTrigger data-testid="finance-kdv-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {KDV_RATES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* KDV Preview */}
            <div className="p-4 rounded-lg bg-slate-50 border space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">KDV Hariç Tutar:</span>
                <span className="font-medium">{formData.amount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">KDV (%{formData.kdv_rate}):</span>
                <span className="font-medium text-blue-600">{previewKdv.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL</span>
              </div>
              <div className="flex justify-between text-sm border-t pt-2">
                <span className="font-semibold">Toplam (KDV Dahil):</span>
                <span className="font-bold text-lg">{previewTotal.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL</span>
              </div>
            </div>

            <div>
              <Label htmlFor="notes">Notlar</Label>
              <Input
                id="notes"
                data-testid="finance-notes-input"
                placeholder="Ek bilgiler..."
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => { setIsAddDialogOpen(false); resetForm(); }}
                data-testid="cancel-finance-btn"
              >
                İptal
              </Button>
              <Button
                type="submit"
                className={`flex-1 ${formData.record_type === "satis" ? "bg-green-600 hover:bg-green-700" : "bg-red-600 hover:bg-red-700"}`}
                data-testid="confirm-finance-btn"
              >
                {formData.record_type === "satis" ? "Satış Ekle" : "Alış Ekle"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

function round2(n) { return Math.round(n * 100) / 100; }

export default Finance;
